import { Injectable } from "@angular/core";
import { AuthenticationService, Config, Token, Claims, Client } from '@mitel/cloudlink-sdk';
import { environment } from '../../../environments/environment';
import { PolicyTestRequest, PolicyTestResponse, AssumeRoleTokenPostRequest, AuthCode } from "@mitel/cloudlink-sdk/authentication";
import { AppStateService } from './app-state.service';
import { PolicyEffects } from "../constants/constants";

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    public auth: AuthenticationService;

    constructor(private appStateService: AppStateService) {
        Config.cloud = environment.cloud;
		this.auth = Config.authentication;
    }

    // curently not used. candidate for removal?
    // public setCloud( cloudType: string )
    // {
    //     Config.cloud = cloudType;
    //     Config.authentication = null;
    //     this.auth = Config.authentication;
    // }

    public getToken(token?: Token): Promise<Token>
    {
        return this.auth.getToken(token);
    }

    public getPartnerClaims(): Promise<Claims>
    {
        // check for an "original_token"
        let token = localStorage.getItem('@mitel/token.original_token');
        if (!token ) {
            // there is no original token, so return null
            return Promise.resolve(null);
        }
        return this.auth.whoAmI( JSON.parse(token) );
    }

    public getPartnerToken(): Token
    {
        let tokenStr = localStorage.getItem('@mitel/token.original_token');
        if ( tokenStr )
            return  JSON.parse(tokenStr); 

        return null;
    }

    public getPartnerAuthHeader(): any {
        let token = this.getPartnerToken();
    
        if ( token )
        {
            const options = {
                headers: {
                    'content-type': 'application/json',
                    authorization: `Bearer ${token.access_token}`
                }
            };
            return options;
        }
        return null;
    }

    assumeRole( newRole: string, newAccountId: string ): Promise<Token>
    {
        console.log("ASSUME ROLE", newAccountId);
        return this.auth.assumeRole({role: newRole, accountId: newAccountId});
    }

    revertRole(): Promise<Token> {
        if ( this.getPartnerToken() != null )
        {
            console.log("REVERT ROLE");
            return this.auth.revertRole();
        }
        else {
            return this.getToken();
        }
    }

    getPolicyForFeature(feature: PolicyTestRequest): Promise<PolicyTestResponse>
    {
        return this.auth.testPolicy(feature);
    }
    
    testPolicy(request: PolicyTestRequest): Promise<PolicyTestResponse> {
        return this.auth.testPolicy(request);
    }

    async doesPolicyForFeatureHaveDesiredEffect(feature: PolicyTestRequest, effect: PolicyEffects): Promise<boolean>{
        return this.getPolicyForFeature(feature).then( response => {
            if ( response && response.results )
            {
                const resourceResponse = response.results.find(res => {
                    return res.resource === feature.resources[0].resource;
                });
                if (resourceResponse && PolicyEffects[resourceResponse.effect] === effect) 
                {
                    return true;
                }
                else 
                    return false;
            }
            else
            {
                return false;
            }
        });
    }

    private getAssumeRoleParams(accountId) {
        let params;
        if (accountId) {
            params = { accountId: accountId, role: 'ACCOUNT_ADMIN' };
        }
        return params;
    }

    private getAssumedRoleOptions(token: Token) {
        const headers = { 'authorization': 'Bearer ' + token.access_token };
        const options = {
            headers: headers
        };

        return options;
    }

    private getAuthOptions(token: Token) {
        const options = {
            headers: {
                'content-type': 'application/json',
                authorization: `Bearer ${token.access_token}`
            }
        }
        return options;
    }

    public getAssumedRoleToken(body: AssumeRoleTokenPostRequest): Promise<Token> {
        return this.auth.getToken().then(token => {
            const authHeader = this.getAuthOptions(token);
            return this.createAssumeRoleToken(body, authHeader)
        })
    }

    public createAssumeRoleToken(body: AssumeRoleTokenPostRequest, options?: any): Promise<Token> {
        return this.auth.createTokenWithAssumedRole({ body, options });
    }

    async getAssumeRoleHeader(accountId) {
        const assumeParams = this.getAssumeRoleParams(accountId);
        if (assumeParams) {
            const token = await this.getAssumedRoleToken(assumeParams);
            return this.getAssumedRoleOptions(token);
        } else {
            return undefined;
        }
    }

    // only needed in time when we have to call a microservice 
    // directly when it is early in the development of a new route
    // public async getRegularAuthHeader(): Promise<any> {
    //     let token = await this.getToken();
    
    //     if ( token )
    //     {
    //         const options = {
    //             headers: {
    //                 'content-type': 'application/json',
    //                 authorization: `Bearer ${token.access_token}`
    //             }
    //         };
    //         return options;
    //     }
    //     return null;
    // }

    getSSODataForAccount( accountId: string ): Promise<any>
    {
        return this.auth.getSamlIdentityProvider(accountId);
    }

    saveSSODataForAccount( accountId: string, entityId: string, replyURL: string,
        signInUrl: string, idpEntityId: string, signingCert: string, type: string ): Promise<any>
    {
        let body: any = {
            type: type,
            active: true,
            saml: {
                issuerId: idpEntityId,
                serviceProviderIssuerId: entityId,
                serviceProviderReplyUrl: replyURL,
                loginUrl: signInUrl,
                certificates: signingCert
            }
         };

        console.log("This is what I will save for the account", body);
        return this.auth.getSamlIdentityProvider(accountId).then( data => {
            // this is an update
            return this.auth.updateSamlIdentityProvider(accountId, body);
        })
        .catch( error => {
            // is it the right error?
            if ( error.statusCode == 404 ) // no previous saml data for that account, so create
            {
                // this is a create
                return this.auth.createSamlIdentityProvider(body);
            }
            else
            {
                throw(error); // this catch will catch both the initial get and the update errors, so we need to throw it
            }
        });
    }

    deleteSSODataForAccount( accountId: string )
    {
        return this.auth.deleteSamlIdentityProvider(accountId);
    }
    
    getIFrameAuthCode( iframeUrl: string, iframeClientId: string ): Promise<string>
    {

    //    (params: { clientId: string; responseType: string; scope?: string; username?: string; password?: string; accountId?: string; redirectUri?: string; nonce?: string; sessionState?: string; codeChallengeMethod?: string; codeChallenge?: string; idTokenHint?: string; targetClientId?: string; options?: any; })

        return this.auth.getToken().then( token => {
            let params = {
                clientId: this.appStateService.getEnvironmentClientId(),
                responseType: 'code',
                redirectUri: iframeUrl,
                targetClientId: iframeClientId,
                idTokenHint: token.refresh_token
            }
            console.log("Requesting auth code for this irframe client", params)
            return this.auth.createAuthorizationCode(params).then( authCode => {
                return authCode.code;
            })
        })

    }

    getCurrentClaims(): Promise<Claims>
    {
        return this.auth.whoAmI();
    }

    createTokenForClient(clientId: string, clientSecret: string) {
        return this.auth.createToken({
            grantType: 'client_credentials',
            clientId,
            clientSecret
        });
    }

    async getClientAuthOptions( client: Client ): Promise<any> {
        let token = await this.createTokenForClient(client.clientId, client.clientSecret);
        const options = {
            headers: {
                authorization: `Bearer ${token.access_token}`
            }
        }
        return options;
    }
}

